import * as React from 'react'
import { useParams, Link } from 'react-router-dom'
import { fetchData } from '../utils/fetchData'
import Page from '../components/layout/Page'
import Content from '../components/layout/Content'
import Arrow from '../icons/Arrow'
import Add from '../icons/Add'
import ProtectedRoute from '../helpers/ProtectedRoute'
import { Image } from '../types/Image'
import { Media } from '../types/User'
import ImageCard from '../components/ImageCard'
import styles from '../styles/Library.module.css'

function Library() {
  const STORAGE_KEY = 'media'
  const localStorageMedia = localStorage.getItem(STORAGE_KEY)
  let media: Media | null = null
  if (localStorageMedia) {
    try {
      media = JSON.parse(localStorageMedia)
    } catch (error) {
      console.log(error)
    }
  }

  const showMore = React.useRef<HTMLDivElement>(null)
  const token = media?.token ?? null
  const { storeId } = useParams()
  const [images, setImages] = React.useState<Image[]>([])
  const [loading, setLoading] = React.useState<boolean>(false)
  const [nextCursor, setNextCursor] = React.useState<string | null>(null)

  const getImages = React.useCallback(async () => {
    setLoading(true)
    try {
      if (nextCursor?.length === 0) {
        return
      }
      const res = await fetchData(
        `/api/cloudinary/tags/global/resources${
          nextCursor ? `/${nextCursor}` : ''
        }`,
        {
          method: 'GET',
        },
      )

      if (res) {
        const { data, status } = res

        if (
          status === 200 &&
          data &&
          data?.resources &&
          data?.resources.length > 0
        ) {
          setImages((prev) => [...prev, ...data.resources])
          setNextCursor(data.next_cursor ? data.next_cursor : '')
        }
      }
    } catch (error) {
      console.log(error)
    }
    setLoading(false)
  }, [nextCursor])

  React.useEffect(() => {
    if (token && storeId && !nextCursor) {
      getImages()
    }
  }, [token, storeId, getImages, nextCursor])

  React.useLayoutEffect(() => {
    showMore?.current?.scrollIntoView({
      behavior: 'smooth',
    })
  }, [images])

  return (
    <ProtectedRoute user={token}>
      <Page>
        <div className={styles.library}>
          <Content>
            <Link className={styles.backButton} to={`/${storeId}`}>
              <Arrow className={styles.arrowIcon} />
              <span>Till startsidan</span>
            </Link>
            <div className={styles.header}>
              <h1 className={styles.heading}>Globala bilder</h1>
              <p className={styles.preamble}>
                Globala bilder i mediabibloteket kan nyttjas av samtliga
                butiker.
              </p>
            </div>
          </Content>
        </div>
        <div className={styles.library}>
          <Content>
            {loading && images.length === 0 ? (
              <div>Laddar…</div>
            ) : (
              <>
                {images.length > 0 ? (
                  <ul className={styles.list}>
                    {images.map((image) => (
                      <ImageCard
                        key={image.asset_id}
                        assetId={image.asset_id}
                        secureUrl={image.secure_url}
                        aspectRatio={image.aspect_ratio}
                        context={image.context}
                      />
                    ))}
                  </ul>
                ) : (
                  <div>Inga bilder</div>
                )}
              </>
            )}
            <div className={styles.pagination} ref={showMore}>
              {nextCursor && nextCursor.length > 0 && (
                <button
                  disabled={loading}
                  className={styles.frontButton}
                  onClick={() => getImages()}
                >
                  {loading && images.length > 0 ? (
                    <span>Laddar…</span>
                  ) : (
                    <>
                      <span>Visa fler</span>
                      <Add className={styles.addIcon} />
                    </>
                  )}
                </button>
              )}
            </div>
          </Content>
        </div>
      </Page>
    </ProtectedRoute>
  )
}

export default Library
